import React, { useState, useEffect } from 'react';

interface UserSegment {
  id: string;
  name: string;
  userCount: number;
  percentage: number;
  growthRate: number;
  characteristics: string[];
  averageEngagement: number;
  revenueContribution: number;
  churnRate: number;
  acquisitionCost: number;
  lifetimeValue: number;
}

interface EngagementMetric {
  id: string;
  name: string;
  value: number;
  previousValue: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  benchmark: number;
  category: 'acquisition' | 'activation' | 'retention' | 'revenue' | 'referral';
}

interface UserJourney {
  stage: string;
  users: number;
  conversionRate: number;
  dropoffRate: number;
  averageTime: number; // minutes
  topActions: Array<{
    action: string;
    frequency: number;
    impact: 'positive' | 'negative' | 'neutral';
  }>;
  improvementOpportunities: string[];
}

interface BehaviorPattern {
  id: string;
  pattern: string;
  description: string;
  userCount: number;
  frequency: number;
  impact: 'high' | 'medium' | 'low';
  category: 'search' | 'browse' | 'purchase' | 'review' | 'social';
  insights: string[];
  recommendations: string[];
}

interface EngagementAnalyticsData {
  overview: {
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
    returningUsers: number;
    averageSessionDuration: number;
    bounceRate: number;
    pageViews: number;
    conversionRate: number;
  };
  segments: UserSegment[];
  metrics: EngagementMetric[];
  userJourney: UserJourney[];
  behaviorPatterns: BehaviorPattern[];
  cohortAnalysis: Array<{
    cohort: string;
    month0: number;
    month1: number;
    month3: number;
    month6: number;
    month12: number;
  }>;
  featureUsage: Array<{
    feature: string;
    adoption: number;
    frequency: number;
    satisfaction: number;
    category: string;
  }>;
  contentEngagement: Array<{
    contentType: string;
    views: number;
    engagement: number;
    shareRate: number;
    completionRate: number;
  }>;
}

interface UserEngagementAnalyticsProps {
  timeframe: '7d' | '30d' | '90d' | '1y';
  userRole: 'admin' | 'staff';
}

export default function UserEngagementAnalytics({ timeframe = '30d', userRole }: UserEngagementAnalyticsProps) {
  const [analyticsData, setAnalyticsData] = useState<EngagementAnalyticsData | null>(null);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'segments' | 'journey' | 'behavior' | 'cohorts' | 'features'>('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEngagementData = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockSegments: UserSegment[] = [
          {
            id: '1',
            name: 'Power Users',
            userCount: 1240,
            percentage: 8.2,
            growthRate: 23.4,
            characteristics: ['High frequency usage', '5+ reviews written', 'Premium subscriptions'],
            averageEngagement: 92,
            revenueContribution: 34.5,
            churnRate: 2.1,
            acquisitionCost: 45,
            lifetimeValue: 890
          },
          {
            id: '2',
            name: 'Regular Researchers',
            userCount: 3820,
            percentage: 25.3,
            growthRate: 18.7,
            characteristics: ['Weekly visits', 'Detailed strain research', 'Lab result focused'],
            averageEngagement: 78,
            revenueContribution: 28.9,
            churnRate: 5.4,
            acquisitionCost: 32,
            lifetimeValue: 420
          },
          {
            id: '3',
            name: 'Casual Browsers',
            userCount: 5650,
            percentage: 37.4,
            growthRate: 12.1,
            characteristics: ['Monthly visits', 'Basic searches', 'Price comparison'],
            averageEngagement: 45,
            revenueContribution: 18.2,
            churnRate: 12.8,
            acquisitionCost: 28,
            lifetimeValue: 125
          },
          {
            id: '4',
            name: 'New Explorers',
            userCount: 2890,
            percentage: 19.1,
            growthRate: 45.2,
            characteristics: ['First-time kratom users', 'Educational content focus', 'Cautious purchasing'],
            averageEngagement: 58,
            revenueContribution: 12.4,
            churnRate: 22.3,
            acquisitionCost: 52,
            lifetimeValue: 180
          },
          {
            id: '5',
            name: 'Vendor Partners',
            userCount: 156,
            percentage: 1.0,
            growthRate: 8.9,
            characteristics: ['Business accounts', 'High value transactions', 'Platform dependency'],
            averageEngagement: 95,
            revenueContribution: 85.7,
            churnRate: 1.2,
            acquisitionCost: 245,
            lifetimeValue: 12500
          }
        ];

        const mockMetrics: EngagementMetric[] = [
          {
            id: '1',
            name: 'Daily Active Users',
            value: 4520,
            previousValue: 3890,
            change: 630,
            changePercent: 16.2,
            trend: 'up',
            benchmark: 4200,
            category: 'activation'
          },
          {
            id: '2',
            name: 'Session Duration',
            value: 8.4,
            previousValue: 7.2,
            change: 1.2,
            changePercent: 16.7,
            trend: 'up',
            benchmark: 6.5,
            category: 'retention'
          },
          {
            id: '3',
            name: 'Feature Adoption Rate',
            value: 67.8,
            previousValue: 62.1,
            change: 5.7,
            changePercent: 9.2,
            trend: 'up',
            benchmark: 65.0,
            category: 'activation'
          },
          {
            id: '4',
            name: 'User Retention (30d)',
            value: 42.5,
            previousValue: 38.9,
            change: 3.6,
            changePercent: 9.3,
            trend: 'up',
            benchmark: 40.0,
            category: 'retention'
          },
          {
            id: '5',
            name: 'Revenue per User',
            value: 23.40,
            previousValue: 19.80,
            change: 3.60,
            changePercent: 18.2,
            trend: 'up',
            benchmark: 21.50,
            category: 'revenue'
          }
        ];

        const mockUserJourney: UserJourney[] = [
          {
            stage: 'Landing',
            users: 15400,
            conversionRate: 100,
            dropoffRate: 0,
            averageTime: 0.5,
            topActions: [
              { action: 'View homepage', frequency: 87, impact: 'neutral' },
              { action: 'Search immediately', frequency: 34, impact: 'positive' },
              { action: 'Browse strain guides', frequency: 28, impact: 'positive' }
            ],
            improvementOpportunities: ['Optimize hero section CTA', 'Improve search visibility']
          },
          {
            stage: 'Discovery',
            users: 12100,
            conversionRate: 78.6,
            dropoffRate: 21.4,
            averageTime: 4.2,
            topActions: [
              { action: 'Search strains', frequency: 76, impact: 'positive' },
              { action: 'Filter by effects', frequency: 52, impact: 'positive' },
              { action: 'Read vendor profiles', frequency: 39, impact: 'positive' }
            ],
            improvementOpportunities: ['Enhanced filter UX', 'Guided product recommendations']
          },
          {
            stage: 'Research',
            users: 8950,
            conversionRate: 74.0,
            dropoffRate: 26.0,
            averageTime: 12.8,
            topActions: [
              { action: 'Read reviews', frequency: 89, impact: 'positive' },
              { action: 'Check lab results', frequency: 67, impact: 'positive' },
              { action: 'Compare vendors', frequency: 45, impact: 'neutral' }
            ],
            improvementOpportunities: ['Streamline comparison tools', 'Highlight verified reviews']
          },
          {
            stage: 'Decision',
            users: 3420,
            conversionRate: 38.2,
            dropoffRate: 61.8,
            averageTime: 6.7,
            topActions: [
              { action: 'Visit vendor site', frequency: 78, impact: 'positive' },
              { action: 'Save for later', frequency: 34, impact: 'neutral' },
              { action: 'Share product', frequency: 12, impact: 'positive' }
            ],
            improvementOpportunities: ['Reduce decision friction', 'Implement wish lists']
          },
          {
            stage: 'Post-Purchase',
            users: 1890,
            conversionRate: 55.3,
            dropoffRate: 44.7,
            averageTime: 3.4,
            topActions: [
              { action: 'Write review', frequency: 34, impact: 'positive' },
              { action: 'Browse related products', frequency: 28, impact: 'positive' },
              { action: 'Join community forums', frequency: 15, impact: 'positive' }
            ],
            improvementOpportunities: ['Incentivize reviews', 'Automated follow-up campaigns']
          }
        ];

        const mockBehaviorPatterns: BehaviorPattern[] = [
          {
            id: '1',
            pattern: 'Evening Research Sessions',
            description: 'Users conduct detailed strain research between 7-10 PM',
            userCount: 3240,
            frequency: 68,
            impact: 'high',
            category: 'browse',
            insights: [
              'Peak engagement occurs during evening hours',
              'Users spend 15+ minutes per session',
              'Higher conversion to reviews and purchases'
            ],
            recommendations: [
              'Schedule content releases for 6-7 PM',
              'Optimize site performance for evening traffic',
              'Create evening-focused email campaigns'
            ]
          },
          {
            id: '2',
            pattern: 'Mobile-First Strain Discovery',
            description: 'Increasing mobile usage for initial strain research',
            userCount: 5890,
            frequency: 72,
            impact: 'high',
            category: 'search',
            insights: [
              'Mobile traffic up 45% in last quarter',
              'Users prefer visual strain guides on mobile',
              'Voice search adoption growing'
            ],
            recommendations: [
              'Prioritize mobile UX improvements',
              'Develop mobile-specific features',
              'Optimize for voice search queries'
            ]
          },
          {
            id: '3',
            pattern: 'Lab Result Verification Behavior',
            description: 'Users increasingly verify lab results before purchasing',
            userCount: 4120,
            frequency: 58,
            impact: 'high',
            category: 'review',
            insights: [
              'Lab result views up 67% this quarter',
              'Users with lab verification have 23% higher conversion',
              'QR code scanning adoption growing rapidly'
            ],
            recommendations: [
              'Make lab results more prominent',
              'Gamify verification process',
              'Educational content about lab importance'
            ]
          },
          {
            id: '4',
            pattern: 'Social Proof Dependency',
            description: 'Heavy reliance on reviews and ratings for decision making',
            userCount: 7650,
            frequency: 84,
            impact: 'high',
            category: 'social',
            insights: [
              'Users read average of 4.2 reviews before purchase',
              'Trusted Tester reviews have 3x more influence',
              'Recent reviews weighted more heavily'
            ],
            recommendations: [
              'Implement review helpfulness voting',
              'Promote Trusted Tester program',
              'Add review freshness indicators'
            ]
          }
        ];

        const mockData: EngagementAnalyticsData = {
          overview: {
            totalUsers: 15100,
            activeUsers: 4520,
            newUsers: 2890,
            returningUsers: 12210,
            averageSessionDuration: 8.4,
            bounceRate: 34.2,
            pageViews: 89400,
            conversionRate: 12.5
          },
          segments: mockSegments,
          metrics: mockMetrics,
          userJourney: mockUserJourney,
          behaviorPatterns: mockBehaviorPatterns,
          cohortAnalysis: [
            { cohort: 'Jan 2024', month0: 100, month1: 68, month3: 45, month6: 32, month12: 0 },
            { cohort: 'Dec 2023', month0: 100, month1: 72, month3: 48, month6: 35, month12: 28 },
            { cohort: 'Nov 2023', month0: 100, month1: 69, month3: 44, month6: 31, month12: 25 },
            { cohort: 'Oct 2023', month0: 100, month1: 65, month3: 42, month6: 29, month12: 23 },
            { cohort: 'Sep 2023', month0: 100, month1: 63, month3: 39, month6: 27, month12: 21 }
          ],
          featureUsage: [
            { feature: 'Strain Search', adoption: 89, frequency: 156, satisfaction: 4.3, category: 'Core' },
            { feature: 'Lab Verification', adoption: 67, frequency: 89, satisfaction: 4.7, category: 'Trust' },
            { feature: 'Vendor Comparison', adoption: 45, frequency: 67, satisfaction: 4.1, category: 'Decision' },
            { feature: 'Review System', adoption: 34, frequency: 23, satisfaction: 4.5, category: 'Social' },
            { feature: 'Trusted Tester Reviews', adoption: 78, frequency: 134, satisfaction: 4.8, category: 'Trust' },
            { feature: 'Price Alerts', adoption: 23, frequency: 45, satisfaction: 3.9, category: 'Utility' }
          ],
          contentEngagement: [
            { contentType: 'Strain Guides', views: 34200, engagement: 67, shareRate: 12, completionRate: 78 },
            { contentType: 'Vendor Profiles', views: 28900, engagement: 45, shareRate: 8, completionRate: 82 },
            { contentType: 'Lab Results', views: 23400, engagement: 89, shareRate: 15, completionRate: 94 },
            { contentType: 'Educational Articles', views: 19800, engagement: 71, shareRate: 18, completionRate: 65 },
            { contentType: 'User Reviews', views: 67800, engagement: 56, shareRate: 9, completionRate: 88 }
          ]
        };

        setAnalyticsData(mockData);
        setLoading(false);
      }, 1400);
    };

    fetchEngagementData();
  }, [timeframe]);

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return '#10b981';
      case 'down': return '#ef4444';
      case 'stable': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      case 'stable': return '➡️';
      default: return '📊';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'acquisition': return '#8b5cf6';
      case 'activation': return '#3b82f6';
      case 'retention': return '#10b981';
      case 'revenue': return '#f59e0b';
      case 'referral': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Analyzing user engagement patterns...</p>
      </div>
    );
  }

  if (!analyticsData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Engagement data not available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          User Engagement Analytics
        </h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          Deep insights into user behavior, journey optimization, and engagement patterns
        </p>
      </div>

      {/* Overview Metrics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '3rem' 
      }}>
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.5rem' }}>
            {analyticsData.overview.totalUsers.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Users</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.5rem' }}>
            {analyticsData.overview.activeUsers.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Daily Active Users</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b', marginBottom: '0.5rem' }}>
            {analyticsData.overview.averageSessionDuration.toFixed(1)}m
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Avg Session Duration</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem' }}>
            {analyticsData.overview.conversionRate.toFixed(1)}%
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Conversion Rate</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem', overflowX: 'auto' }}>
            {(['overview', 'segments', 'journey', 'behavior', 'cohorts', 'features'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                style={{
                  padding: '1rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: selectedTab === tab ? '3px solid #3b82f6' : '3px solid transparent',
                  color: selectedTab === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: selectedTab === tab ? '600' : '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab === 'journey' ? 'User Journey' : tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {selectedTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
          {/* Key Metrics */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Key Engagement Metrics
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {analyticsData.metrics.map(metric => (
                <div key={metric.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.5rem',
                  border: '1px solid #4a5568'
                }}>
                  <div style={{ flex: '1' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                        {metric.name}
                      </h4>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.125rem 0.375rem',
                        backgroundColor: getCategoryColor(metric.category) + '20',
                        color: getCategoryColor(metric.category),
                        borderRadius: '0.25rem',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        {metric.category}
                      </span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e2e8f0' }}>
                      {metric.name.includes('Duration') ? `${metric.value}min` : 
                       metric.name.includes('Rate') || metric.name.includes('Revenue') ? 
                       (metric.name.includes('Revenue') ? `$${metric.value}` : `${metric.value}%`) : 
                       metric.value.toLocaleString()}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '1rem', 
                      fontWeight: 'bold', 
                      color: getTrendColor(metric.trend),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      justifyContent: 'flex-end'
                    }}>
                      {metric.changePercent > 0 ? '+' : ''}{metric.changePercent.toFixed(1)}%
                      <span style={{ fontSize: '0.875rem' }}>{getTrendIcon(metric.trend)}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      vs benchmark: {metric.benchmark}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Engagement */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              Content Engagement
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {analyticsData.contentEngagement.map((content, index) => (
                <div key={index} style={{ 
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.375rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {content.contentType}
                    </h4>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {content.views.toLocaleString()} views
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.75rem' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Engagement: </span>
                      <span style={{ fontWeight: '600', color: '#e2e8f0' }}>{content.engagement}%</span>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Completion: </span>
                      <span style={{ fontWeight: '600', color: '#e2e8f0' }}>{content.completionRate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'segments' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {analyticsData.segments.map(segment => (
            <div key={segment.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div style={{ flex: '1' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>
                    {segment.name}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {segment.userCount.toLocaleString()} users ({segment.percentage.toFixed(1)}%)
                    </span>
                    <span style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '600',
                      color: '#10b981'
                    }}>
                      +{segment.growthRate.toFixed(1)}% growth
                    </span>
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.25rem' }}>
                    ${segment.lifetimeValue}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Lifetime Value</div>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10b981' }}>
                    {segment.averageEngagement}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Engagement Score</div>
                </div>
                
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f59e0b' }}>
                    {segment.revenueContribution.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Revenue Share</div>
                </div>
                
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: segment.churnRate > 10 ? '#ef4444' : '#10b981' }}>
                    {segment.churnRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Churn Rate</div>
                </div>
                
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                    ${segment.acquisitionCost}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Acquisition Cost</div>
                </div>
              </div>

              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                  Key Characteristics
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {segment.characteristics.map((characteristic, index) => (
                    <span key={index} style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#3b82f620',
                      color: '#3b82f6',
                      borderRadius: '0.25rem'
                    }}>
                      {characteristic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'journey' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {/* Journey Funnel */}
          <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
              User Journey Funnel
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem', overflowX: 'auto' }}>
              {analyticsData.userJourney.map((stage, index) => (
                <React.Fragment key={stage.stage}>
                  <div style={{ 
                    minWidth: '150px',
                    textAlign: 'center',
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.5rem',
                    border: '2px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.25rem' }}>
                      {stage.users.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem' }}>
                      {stage.stage}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {stage.conversionRate.toFixed(1)}% conversion
                    </div>
                    {stage.dropoffRate > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                        {stage.dropoffRate.toFixed(1)}% drop-off
                      </div>
                    )}
                  </div>
                  {index < analyticsData.userJourney.length - 1 && (
                    <div style={{ fontSize: '1.5rem', color: '#6b7280' }}>→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Stage Details */}
          {analyticsData.userJourney.map(stage => (
            <div key={stage.stage} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                  {stage.stage} Stage
                </h4>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#3b82f6' }}>
                    {stage.averageTime.toFixed(1)} min avg time
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '1.5rem' 
              }}>
                {/* Top Actions */}
                <div>
                  <h5 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Top User Actions
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {stage.topActions.map((action, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.5rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0.25rem'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: '#e2e8f0' }}>{action.action}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>
                            {action.frequency}%
                          </span>
                          <span style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.125rem 0.25rem',
                            backgroundColor: action.impact === 'positive' ? '#10b98120' : action.impact === 'negative' ? '#ef444420' : '#6b728020',
                            color: action.impact === 'positive' ? '#10b981' : action.impact === 'negative' ? '#ef4444' : '#6b7280',
                            borderRadius: '0.125rem'
                          }}>
                            {action.impact}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Improvement Opportunities */}
                <div>
                  <h5 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Improvement Opportunities
                  </h5>
                  <ul style={{ margin: '0', paddingLeft: '1rem', color: '#a0aec0' }}>
                    {stage.improvementOpportunities.map((opportunity, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.4' }}>
                        {opportunity}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'behavior' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {analyticsData.behaviorPatterns.map(pattern => (
            <div key={pattern.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div style={{ flex: '1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {pattern.pattern}
                    </h3>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getImpactColor(pattern.impact) + '20',
                      color: getImpactColor(pattern.impact),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {pattern.impact} Impact
                    </span>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#3b82f620',
                      color: '#3b82f6',
                      borderRadius: '0.25rem',
                      textTransform: 'capitalize'
                    }}>
                      {pattern.category}
                    </span>
                  </div>
                  <p style={{ fontSize: '1rem', color: '#a0aec0', margin: '0', lineHeight: '1.6' }}>
                    {pattern.description}
                  </p>
                </div>
                
                <div style={{ textAlign: 'right', minWidth: '120px' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.25rem' }}>
                    {pattern.userCount.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>Users</div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#10b981' }}>
                    {pattern.frequency}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Frequency</div>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '1.5rem' 
              }}>
                {/* Insights */}
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Key Insights
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '1rem', color: '#a0aec0' }}>
                    {pattern.insights.map((insight, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recommendations */}
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                    Recommendations
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '1rem', color: '#a0aec0' }}>
                    {pattern.recommendations.map((recommendation, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {recommendation}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'cohorts' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            User Retention Cohort Analysis
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Cohort</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Month 0</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Month 1</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Month 3</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Month 6</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Month 12</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.cohortAnalysis.map(cohort => (
                  <tr key={cohort.cohort} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
                      {cohort.cohort}
                    </td>
                    {[cohort.month0, cohort.month1, cohort.month3, cohort.month6, cohort.month12].map((value, index) => (
                      <td key={index} style={{ padding: '1rem 0.75rem', textAlign: 'center' }}>
                        {value > 0 ? (
                          <div style={{ 
                            display: 'inline-block',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            backgroundColor: `hsl(${value * 1.2}, 70%, 95%)`,
                            color: `hsl(${value * 1.2}, 70%, 30%)`,
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {value}%
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div style={{ 
            marginTop: '1.5rem',
            padding: '1rem', 
            backgroundColor: '#eff6ff', 
            borderRadius: '0.5rem',
            border: '1px solid #bfdbfe'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
              Retention Insights
            </h4>
            <ul style={{ margin: '0', paddingLeft: '1rem', color: '#1e40af', fontSize: '0.875rem' }}>
              <li>Average 1-month retention: 67%</li>
              <li>Average 6-month retention: 31%</li>
              <li>Recent cohorts showing improved retention</li>
              <li>Power user segment has 85%+ 12-month retention</li>
            </ul>
          </div>
        </div>
      )}

      {selectedTab === 'features' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Feature Adoption & Usage
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {analyticsData.featureUsage.map((feature, index) => (
              <div key={index} style={{ 
                padding: '1.5rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.5rem',
                border: '1px solid #4a5568'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.25rem 0' }}>
                      {feature.feature}
                    </h4>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#3b82f620',
                      color: '#3b82f6',
                      borderRadius: '0.25rem'
                    }}>
                      {feature.category}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10b981' }}>
                      {feature.adoption}%
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Adoption</div>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Usage Frequency</div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0' }}>
                      {feature.frequency}/month
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Satisfaction</div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#f59e0b' }}>
                      ★ {feature.satisfaction}/5
                    </div>
                  </div>
                </div>
                
                {/* Usage bar */}
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  backgroundColor: '#e5e7eb', 
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${feature.adoption}%`, 
                    height: '100%', 
                    backgroundColor: feature.adoption >= 70 ? '#10b981' : feature.adoption >= 40 ? '#f59e0b' : '#ef4444',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}